import { Grid, PasswordInput, Button, Text } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import { GoogleLogo } from "@components/Logos/GoogleLogo.tsx";
import type { Password } from "../../../types/password";
import { useGoogleToken } from "../hooks/useGoogleToken";
import { validatePasswordChange } from "../utils/passwordValidation";
import { unlinkGoogleAccount } from "@pages/LoginManagement/api.ts";
import { useAuthStore } from "@utils/stores/authStore.ts";

interface GoogleUnlinkFormProps {
  password: Password;
  setPassword: React.Dispatch<React.SetStateAction<Password>>;
  onSuccess: () => void;
  onError: (error: string) => void;
  onCancel: () => void;
  setNotification: (notification: {
    title: string;
    message: React.ReactNode;
    color: string;
  }) => void;
  setError: (error: { newPassword: string; confirmPassword: string }) => void;
  error: { newPassword: string; confirmPassword: string };
}

/**
 * Form component for unlinking Google account and setting a password.
 * Handles the complete flow including Google token verification.
 */
export const GoogleUnlinkForm = ({
  password,
  setPassword,
  onSuccess,
  onError,
  onCancel,
  setNotification,
  setError,
  error,
}: GoogleUnlinkFormProps) => {
  const { waitingForGoogleToken, requestGoogleToken, cancelGoogleToken } =
    useGoogleToken();
  const refreshAuth = useAuthStore((state) => state.refreshAuth);

  const handleUnlinkGoogle = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validatePasswordChange(
      undefined,
      password?.newPassword,
      password?.confirmPassword
    );

    if (validationErrors.newPassword || validationErrors.confirmPassword) {
      setError(validationErrors);
      return;
    }

    setError({ newPassword: "", confirmPassword: "" });

    setNotification({
      title: "Verify Google Account",
      message:
        "Please click the 'Sign in with Google' button below to verify your account ownership.",
      color: "blue",
    });

    try {
      // Request Google token - set up callback and show button
      const idToken = await requestGoogleToken();

      setNotification({
        title: "Unlinking Google Account",
        message: "Setting password and unlinking Google account...",
        color: "blue",
      });

      await unlinkGoogleAccount(password.newPassword, idToken);

      setNotification({
        title: "Success",
        message: "Google account unlinked and password set successfully",
        color: "green",
      });

      // Refresh auth to update auth method
      await refreshAuth();

      // Reset form
      setPassword({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      onSuccess();
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || error.message || "An error occurred";
      onError(errorMessage);
      cancelGoogleToken();
    }
  };

  const handleCancel = () => {
    cancelGoogleToken();
    setPassword({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setError({ newPassword: "", confirmPassword: "" });
    onCancel();
  };

  return (
    <form onSubmit={handleUnlinkGoogle} className="change-password">
      <Grid className="change-password-grid" mt="md">
        <Grid.Col>
          <PasswordInput
            label="New Password"
            placeholder="Enter a new password"
            name="newPassword"
            error={error.newPassword}
            onChange={(e) =>
              setPassword({
                ...password,
                newPassword: e.target.value,
              })
            }
            leftSection={<IconLock color="#A3A9B0" />}
            value={password?.newPassword}
            required
            styles={{
              error: {
                wordBreak: "break-word",
                whiteSpace: "normal",
              },
            }}
          />
        </Grid.Col>
        <Grid.Col>
          <PasswordInput
            label="Confirm New Password"
            placeholder="Re-enter new password"
            name="confirmPassword"
            error={error.confirmPassword}
            onChange={(e) =>
              setPassword({
                ...password,
                confirmPassword: e.target.value,
              })
            }
            value={password?.confirmPassword}
            leftSection={<IconLock color="#A3A9B0" />}
            required
            styles={{
              error: {
                wordBreak: "break-word",
                whiteSpace: "normal",
              },
            }}
          />
        </Grid.Col>
        <Grid.Col>
          {waitingForGoogleToken ? (
            <div>
              <Text size="sm" c="dimmed" mb="sm">
                Click the button below to verify your Google account:
              </Text>
              <div
                id="google-signin-button-container"
                style={{ marginBottom: "10px" }}
              />
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                radius="md"
                mt="sm"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: "10px" }}>
              <Button
                type="submit"
                color="red"
                radius="md"
                c="black"
                variant="light"
                leftSection={<GoogleLogo width={18} height={18} />}
              >
                Unlink Google Account
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                radius="md"
              >
                Cancel
              </Button>
            </div>
          )}
        </Grid.Col>
      </Grid>
    </form>
  );
};
